import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GradeModel, GradeResponse } from "model";

import { fetchRequestAttribute } from "~utils";

import { computeInitialGradeSelected } from "./utils";

export const useGrades = (initialGradeSelected: string[], testData?: GradeModel[]) => {
  // validatedInitialData here takes the user's preferred subjects and grades
  // and coerces it into the list that the grade renderer will accept. Since
  // there are many existing records of grades and subjects being transacted
  // in that don't fit the list, these values will get sorted into "Unknown"

  const [validatedInitialData, setValidatedInitialData] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  const { data: gradesData } = useQuery({
    queryKey: ["fetchRequestAttributeGrades"],
    queryFn: async () => {
      const data = await fetchRequestAttribute<GradeResponse>({ key: "grades" });
      const gradesData = data?.data?.options?.map((grade) => new GradeModel(grade)) || [];

      const validGrades = gradesData.map((item: GradeModel) => item.label);
      const validInitialData = computeInitialGradeSelected(validGrades, initialGradeSelected);
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
      return gradesData;
    },
    enabled: !testData, // if testData exists, do not execute the hook api call
    initialData: [],
  });

  // Update selected grades when initialGradeSelected changes (e.g., teacher selection)
  useEffect(() => {
    if (gradesData?.length > 0) {
      const validGrades = gradesData.map((item: GradeModel) => item.label);
      const validInitialData = computeInitialGradeSelected(validGrades, initialGradeSelected);
      setValidatedInitialData(validInitialData);
      setSelected(validInitialData);
    }
  }, [initialGradeSelected, gradesData]);

  if (testData) return { selected, setSelected, validatedInitialData, gradesData: testData };
  return { selected, setSelected, validatedInitialData, gradesData };
};
